import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { router } from '@/app/routes/router'
import '@/config/zod-error-map'
import { initSentry } from '@/lib/sentry'
import '@/styles/index.css'

const PRELOAD_ERROR_RELOAD_KEY = 'propalar:preload-error-reload-at'
const PRELOAD_ERROR_RELOAD_COOLDOWN_MS = 10_000

interface VitePreloadErrorEvent extends Event {
  payload?: unknown
}

function setupPreloadErrorRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const preloadEvent = event as VitePreloadErrorEvent
    const payloadMessage =
      preloadEvent.payload instanceof Error
        ? preloadEvent.payload.message
        : String(preloadEvent.payload ?? '')

    const shouldRecover =
      payloadMessage.includes('dynamically imported module') ||
      payloadMessage.includes('Unable to preload CSS')

    if (!shouldRecover) return

    const now = Date.now()
    const lastReloadAt = Number(sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY) ?? '0')

    if (now - lastReloadAt < PRELOAD_ERROR_RELOAD_COOLDOWN_MS) return

    preloadEvent.preventDefault()
    sessionStorage.setItem(PRELOAD_ERROR_RELOAD_KEY, String(now))
    window.location.reload()
  })
}

setupPreloadErrorRecovery()
initSentry()

const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Failed to find the root element')
}

createRoot(rootElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
)
